import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { IPatientInfo } from "../../../../@types/Patient";
import {
  ChartTitle,
  StyledSubtitle,
  StyledTile,
} from "../../../../components/ChartCard/ChartCard.styles";
import { ChartCardLoading } from "../../../../components/ChartCard/ChartCardLoading";
import { ChartSpecificValues } from "../../../../components/ChartCard/ChartSpecificValues/ChartSpecificValues";
import { Tag } from "../../../../components/Tag";
import { useEcgRecordings } from "../../../../hooks/queries/ecg";
import { useModal } from "../../../../hooks/useModal";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import {
  getEcgClassificationLabel,
  getEcgClassificationVariant,
} from "../../../../utils/ecg/ecgClassification";
import { EcgResultsModal } from "../EcgResultsModal";
import { DATE_FILTER_ALL } from "../MeasurementModal/components/CustomOptionsBar";
import { CardDataContainer, Divider, RecordingRow } from "./EcgCard.styles";

export type EcgCardProps = {
  patient: IPatientInfo;
};

const MAX_PREVIEW_ROWS = 3;

export function EcgCard({ patient }: EcgCardProps) {
  const { t } = useTranslation();

  const { getDateRangeFilter } = useUserPreferences();
  const { openModal } = useModal();

  // Use the shared, global date range (the same one the option bar drives for
  // the other cards), so picking a range there filters this card too. "ALL"
  // sends no bounds; any other range is sent so the query refetches on change
  // (fromDate/toDate are part of the query key).
  const dateRangeFilter = getDateRangeFilter();
  const isAll = dateRangeFilter.id === DATE_FILTER_ALL;
  const fromDate = isAll
    ? undefined
    : dateRangeFilter.startDate.format("YYYY-MM-DD");
  const toDate = isAll
    ? undefined
    : dateRangeFilter.endDate.format("YYYY-MM-DD");

  const { recordings, isFetching, isRefetching } = useEcgRecordings(
    patient.id,
    fromDate,
    toDate
  );

  const isLoading = isFetching && !isRefetching;
  const isEmpty = recordings.length === 0;
  const latest = recordings[0];
  const previewRecordings = recordings.slice(0, MAX_PREVIEW_ROWS);

  const handleClick = () => {
    openModal(<EcgResultsModal patientId={patient.id} />, {
      width: "920px",
      maxWidth: "96vw",
      height: "676px",
      showCloseButton: true,
      padding: "12px 0 0 24px",
    });
  };

  return (
    <StyledTile onClick={handleClick}>
      {isLoading ? (
        <ChartCardLoading />
      ) : (
        <>
          <ChartTitle>ECG (AliveCor)</ChartTitle>
          {isEmpty ? (
            <StyledSubtitle>{t("patientPage_ecgCard_empty")}</StyledSubtitle>
          ) : null}

          <CardDataContainer>
            {previewRecordings.map((recording, index) => (
              <div key={recording.id} style={{ width: "100%" }}>
                <RecordingRow>
                  <p>{dayjs(recording.recordedAt).format("MMM DD - HH:mm")}</p>
                  <Tag
                    variant={getEcgClassificationVariant(
                      recording.classification
                    )}
                    clickable={false}
                  >
                    {getEcgClassificationLabel(recording.classification)}
                  </Tag>
                </RecordingRow>

                {index !== previewRecordings.length - 1 ? <Divider /> : null}
              </div>
            ))}
          </CardDataContainer>

          <ChartSpecificValues
            values={
              latest
                ? [
                    {
                      label: t("chart_legend_latest"),
                      value: getEcgClassificationLabel(latest.classification),
                    },
                  ]
                : []
            }
          />
        </>
      )}
    </StyledTile>
  );
}
